// AddToSchedule — put something on the schedule without starting a conversation (Oct 23 2026, Ryan: "Maybe you are
// mid working on something and want to put something on there"). A reminder for you, or something for the EmCog to
// do — once, or as a routine. The EmCog hears about it as something you did.
// Oct 31 2026 (Ryan: "Also need a weekend day option, and it needs more typing space … there is no way to see or edit
// its properties from the schedule entry"): a real text box for a routine's instructions, Weekends and chosen days,
// and the same form opens an entry to look at, change or take off.
import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';

/** An item opened from the Schedule: what the server says it is now. */
export interface ScheduleDetail {
  id: string; kind: string; store: 'event' | 'task'; editable: boolean; text: string; forWhom: 'person' | 'emcog';
  at: number | null; repeat: string; days?: number[] | null; repeatHuman?: string | null; from?: string | null; firedCount?: number; lastFired?: number | null;
}

interface Props {
  emcogName: string;
  onClose: () => void;
  onAdded: (text: string) => void;
  /** Open an existing item: shown as it is, changeable when it's a reminder, promise or routine. */
  existing?: ScheduleDetail;
  onRemove?: () => void;
}

const pad = (n: number) => String(n).padStart(2, '0');
const localDate = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const localTime = (d: Date) => `${pad(d.getHours())}:${pad(d.getMinutes())}`;
const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const AddToSchedule: React.FC<Props> = ({ emcogName, onClose, onAdded, existing, onRemove }) => {
  const { token } = useAuth();
  const start = existing?.at ? new Date(existing.at) : (() => { const d = new Date(Date.now() + 60 * 60 * 1000); d.setMinutes(0, 0, 0); return d; })();
  const [what, setWhat] = useState(existing?.text || '');
  const [forWhom, setForWhom] = useState<'me' | 'emcog'>(existing?.forWhom === 'emcog' ? 'emcog' : 'me');
  const [date, setDate] = useState(localDate(start));
  const [time, setTime] = useState(localTime(start));
  const [repeat, setRepeat] = useState(existing?.repeat && existing.repeat !== 'interval' ? existing.repeat : 'none');
  const [days, setDays] = useState<number[]>(existing?.days && existing.days.length ? existing.days : [start.getDay()]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const first = useRef<HTMLTextAreaElement>(null);
  const editing = !!existing;
  const readOnly = !!existing && !existing.editable;
  useEffect(() => { if (!readOnly) first.current?.focus(); const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); }; window.addEventListener('keydown', esc); return () => window.removeEventListener('keydown', esc); }, [onClose, readOnly]);

  const toggleDay = (d: number) => setDays(cur => cur.includes(d) ? cur.filter(x => x !== d) : [...cur, d].sort((a, b) => a - b));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (readOnly) return;
    if (!what.trim()) { setError('What should go on the schedule?'); return; }
    const at = new Date(`${date}T${time}`);
    if (isNaN(at.getTime())) { setError('Pick a date and a time.'); return; }
    if (repeat === 'none' && at.getTime() <= Date.now()) { setError('That time has already passed.'); return; }
    if (repeat === 'days' && !days.length) { setError('Pick at least one day.'); return; }
    setBusy(true); setError(null);
    try {
      const url = editing ? `/api/workspace/schedule/${existing!.kind}/${encodeURIComponent(existing!.id)}` : '/api/workspace/schedule';
      const res = await fetch(url, { method: editing ? 'PATCH' : 'POST', headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) }, body: JSON.stringify({ what: what.trim(), at: at.toISOString(), repeat, days: repeat === 'days' ? days : undefined, forWhom }) });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || `It wasn't ${editing ? 'changed' : 'added'} (${res.status}).`);
      window.dispatchEvent(new Event('emcog:schedule-changed'));
      onAdded(body.alreadyExisted ? `Already on the schedule — kept the one that's there` : editing ? `Changed — ${emcogName} knows` : `Added — ${emcogName} knows`);
      onClose();
    } catch (err) { setError(err instanceof Error ? err.message : String(err)); setBusy(false); }
  };

  return (
    <div className="ws-scrim" onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}>
      <form className="ws-modal ws-modal-wide" onSubmit={submit} aria-labelledby="ws-add-title">
        <h3 id="ws-add-title">{readOnly ? 'On the schedule' : editing ? 'Change this' : 'Add to the schedule'}</h3>
        {editing && (
          <div className="ws-sched-meta">
            {existing!.repeatHuman ? <span>Repeats {existing!.repeatHuman}</span> : <span>Once</span>}
            {existing!.at ? <span>Next: {new Date(existing!.at).toLocaleString([], { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</span> : null}
            {existing!.firedCount ? <span>Done {existing!.firedCount}×{existing!.lastFired ? `, last ${new Date(existing!.lastFired).toLocaleDateString([], { month: 'short', day: 'numeric' })}` : ''}</span> : null}
            {existing!.from ? <span title={existing!.from}>From: {existing!.from}</span> : null}
          </div>
        )}
        <label htmlFor="ws-add-what">What
          <textarea id="ws-add-what" ref={first} value={what} onChange={e => setWhat(e.target.value)} rows={6} maxLength={2000} readOnly={readOnly}
            placeholder={`Call the dentist · Check the injury report\nOr a whole routine: "Give me my morning outlook — headlines, AI news, the Gators, the weather, my to-dos…"`} />
          <span className="ws-count">{what.length}/2000</span>
        </label>
        <div className="ws-seg" role="radiogroup" aria-label="Who it's for">
          <button type="button" role="radio" aria-checked={forWhom === 'me'} disabled={readOnly} onClick={() => setForWhom('me')}>Remind me</button>
          <button type="button" role="radio" aria-checked={forWhom === 'emcog'} disabled={readOnly} onClick={() => setForWhom('emcog')}>For {emcogName} to do</button>
        </div>
        {!readOnly && (<>
          <div className="ws-row2">
            <label htmlFor="ws-add-date">{repeat === 'none' ? 'Date' : 'Starting'}<input id="ws-add-date" type="date" value={date} onChange={e => setDate(e.target.value)} /></label>
            <label htmlFor="ws-add-time">Time<input id="ws-add-time" type="time" value={time} onChange={e => setTime(e.target.value)} /></label>
          </div>
          <label htmlFor="ws-add-repeat">Repeats
            <select id="ws-add-repeat" value={repeat} onChange={e => setRepeat(e.target.value)}>
              <option value="none">Just once</option>
              <option value="daily">Every day</option>
              <option value="weekdays">Every weekday (Mon–Fri)</option>
              <option value="weekends">Every weekend day (Sat & Sun)</option>
              <option value="days">On the days I pick</option>
              <option value="weekly">Every week on this day</option>
              <option value="monthly">Every month on this date</option>
            </select>
          </label>
          {repeat === 'days' && (
            <div className="ws-days" role="group" aria-label="Which days">
              {DAYS.map((d, i) => <button key={d} type="button" aria-pressed={days.includes(i)} onClick={() => toggleDay(i)}>{d}</button>)}
            </div>
          )}
        </>)}
        {readOnly && existing?.repeatHuman && <div className="ws-sched-meta"><span>{existing.repeatHuman} — ask {emcogName} in the chat to change how often.</span></div>}
        {error && <div className="ws-form-error" role="alert">{error}</div>}
        <div className="ws-modal-acts">
          {editing && onRemove && <button type="button" className="ws-btn ws-danger" onClick={() => { onClose(); onRemove(); }}>Take it off</button>}
          <span className="ws-grow" />
          <button type="button" className="ws-btn" onClick={onClose}>{readOnly ? 'Close' : 'Cancel'}</button>
          {!readOnly && <button type="submit" className="ws-btn ws-primary" disabled={busy}>{busy ? (editing ? 'Saving…' : 'Adding…') : (editing ? 'Save changes' : 'Add to schedule')}</button>}
        </div>
      </form>
    </div>
  );
};

export default AddToSchedule;
